<template>
    <div class="tk-page">
        <!-- ─────────── HEADER ─────────── -->
        <header class="tk-header">
            <div class="tk-header__inner">
                <div class="tk-header__title-wrap">
                    <h1 class="tk-title">פניות</h1>
                    <span class="tk-total-pill" :title="`סה״כ ${totalTickets} פניות`">
                        <v-icon size="14">mdi-ticket-confirmation-outline</v-icon>
                        <span>{{ totalTickets.toLocaleString('he-IL') }}</span>
                    </span>
                </div>
            </div>
        </header>

        <ProgressShow v-if="progressShow" v-model="progressShow"></ProgressShow>

        <main class="tk-main">

            <!-- TOOLBAR -->
            <div class="tk-toolbar">
                <div class="tk-search">
                    <v-icon class="tk-search__icon" size="19">mdi-magnify</v-icon>
                    <input v-model="search" type="text" class="tk-search__input"
                        placeholder="חיפוש בפניות לפי נושא או תוכן…" @keyup.enter="getSearchTickets"
                        @input="onSearchInput" />
                    <button v-if="search" type="button" class="tk-search__clear" @click="search = ''; onSearchInput()"
                        aria-label="נקה חיפוש">
                        <v-icon size="14">mdi-close</v-icon>
                    </button>
                </div>

                <div class="tk-filter-chips" role="tablist" aria-label="סינון פניות">
                    <button v-for="t in typesList" :key="t.value" type="button" role="tab" class="tk-chip"
                        :class="{ 'tk-chip--active': filterType === t.value }" :aria-selected="filterType === t.value"
                        @click="filterType = t.value">
                        <span class="tk-chip__dot" :class="`tk-chip__dot--${t.value}`"></span>
                        {{ t.hebrewName }}
                    </button>
                </div>

                <div class="tk-pagesize">
                    <label for="tk-pagesize-select">שורות</label>
                    <select id="tk-pagesize-select" class="tk-pagesize__select" v-model.number="itemsPerPage">
                        <option v-for="n in [20, 50, 100, 500, 1000]" :key="n" :value="n">{{ n }}</option>
                    </select>
                </div>
            </div>

            <!-- TICKETS LIST -->
            <div class="tk-list">
                <template v-if="tickets.length">
                    <article v-for="(ticket, index) in tickets" :key="ticket._id" class="tk-ticket" :class="{
                        'tk-ticket--unread': isUnread(ticket),
                        'tk-ticket--closed': ticket.status === 'close'
                    }" @click="navigateToTicket(ticket._id)" :style="{ '--i': Math.min(index, 12) }">

                        <!-- Side accent strip (RTL leading edge) -->
                        <div class="tk-ticket__strip" aria-hidden="true"></div>

                        <div class="tk-ticket__body">
                            <!-- Number badge -->
                            <div class="tk-ticket__num"
                                :class="{ 'tk-ticket__num--closed': ticket.status === 'close' }">
                                {{ calculatePosition(index) }}
                            </div>

                            <!-- Main content -->
                            <div class="tk-ticket__content">
                                <div class="tk-ticket__header-row">
                                    <span class="tk-ticket__id"
                                        :class="{ 'tk-ticket__id--closed': ticket.status === 'close' }">
                                        #{{ ticket.integer_id }}
                                    </span>
                                    <h3 class="tk-ticket__subject">
                                        <span v-if="isUnread(ticket)" class="tk-ticket__unread-dot"
                                            aria-label="לא נקרא"></span>
                                        {{ ticket.subject }}
                                    </h3>
                                </div>

                                <div class="tk-ticket__preview">
                                    <template v-if="isFileUrl(ticket.responses[ticket.responses.length - 1].message)">
                                        <v-icon size="14" class="tk-ticket__preview-icon">mdi-paperclip</v-icon>
                                        <span class="tk-ticket__file-indicator">קובץ מצורף</span>
                                    </template>
                                    <template v-else>
                                        {{ ticket.responses[ticket.responses.length - 1].message.slice(0, 150) }}…
                                    </template>
                                </div>
                            </div>

                            <!-- Side meta: date + status pills -->
                            <div class="tk-ticket__side">
                                <div class="tk-ticket__date">
                                    <v-icon size="12">mdi-clock-outline</v-icon>
                                    <span>{{ formatDate(ticket.createdAt) }}</span>
                                </div>

                                <div class="tk-ticket__pills">
                                    <span class="tk-pill"
                                        :class="ticket.status === 'close' ? 'tk-pill--closed' : 'tk-pill--open'">
                                        <v-icon size="11">{{ ticket.status === 'close' ? 'mdi-lock-outline' :
                                            'mdi-lock-open-variant-outline'
                                            }}</v-icon>
                                        {{ ticket.status === 'close' ? 'סגור' : 'פתוח' }}
                                    </span>

                                    <span class="tk-pill"
                                        :class="isUnread(ticket) ? 'tk-pill--unread' : 'tk-pill--read'">
                                        <v-icon size="11">{{ isUnread(ticket) ? 'mdi-email-outline' :
                                            'mdi-email-open-outline' }}</v-icon>
                                        {{ isUnread(ticket) ? 'לא נקרא' : 'נקרא' }}
                                    </span>
                                </div>
                            </div>

                            <!-- Chevron for "go to ticket" affordance -->
                            <div class="tk-ticket__chevron" aria-hidden="true">
                                <v-icon size="20">mdi-chevron-left</v-icon>
                            </div>
                        </div>
                    </article>
                </template>

                <!-- EMPTY -->
                <div v-else class="tk-empty">
                    <div class="tk-empty__icon">
                        <v-icon size="40">mdi-ticket-outline</v-icon>
                    </div>
                    <h3 class="tk-empty__title">לא נמצאו פניות</h3>
                    <p class="tk-empty__hint">{{ search ? 'נסה לשנות את החיפוש' : 'אין פניות במערכת כרגע' }}</p>
                </div>
            </div>

            <!-- PAGINATION -->
            <div v-if="totalTickets > itemsPerPage" class="tk-pagination-wrap">
                <v-pagination v-model="currentPage" :length="Math.ceil(totalTickets / itemsPerPage)" :total-visible="7"
                    circle></v-pagination>
            </div>
        </main>

        <SnackBar v-model="snackbar" :snacbarText="snacbarText" :snackbarColorBt="snackbarColorBt"></SnackBar>
    </div>
</template>

<script>
import ProgressShow from '@/components/widgets/progressShow.vue'
import Auth from '../../services/auth.service'
import SnackBar from '@/components/widgets/snackBar.vue'
import ApiServices from '@/services/api.service'
import { mapState } from 'vuex'
import _ from 'lodash'; // Import lodash or use another debounce utility

export default {
    name: 'TicketsListComp',
    components: {
        ProgressShow,
        SnackBar
    },
    data() {
        return {
            tickets: [],
            progressShow: false,
            totalTickets: 0,
            currentPage: 1,
            itemsPerPage: 20,
            snackbar: false,
            filter: "",
            snackbarColorBt: "success",
            snacbarText: "",
            filterType: "all",
            typesList: [
                { hebrewName: "הצג הכל", value: "all" },
                { hebrewName: "לא נקראו", value: "unread" },
                { hebrewName: "נסגרו", value: "close" }
            ],
            search: "",
            userRole: Auth.getUserRole(),
        }
    },
    computed: {
        ...mapState({
            newTicket: state => state.newTicket,
            newResponse: state => state.newResponse,
            ticketId: state => state.ticketId,
        }),
        isAdmin() {
            return Auth.getUserRole() === 'admin'
        },
    },
    watch: {
        newTicket(newValue) {
            this.tickets.unshift(newValue)
        },
        newResponse(newValue) {
            const ticket = this.tickets.findIndex((ticket) => ticket._id === this.ticketId)
            this.tickets[ticket].responses.push(newValue)
        },
        itemsPerPage() {
            this.getTicketsList()
        },
        currentPage() {
            this.getTicketsList()
        },
        filterType() {
            this.getTicketsList()
        }
    },
    methods: {
        getUserRole() {
            return Auth.getUserRole()
        },
        showSnackBar(message, color) {
            this.snacbarText = message
            this.snackbarColorBt = color
            this.snackbar = true
        },
        async getTicketsList() {
            try {
                this.progressShow = true
                let token = localStorage.getItem("token")
                const api = `${process.env.VUE_APP_BASE_URL}/tickets/get_tickets_list?page=${this.currentPage}&limit=${this.itemsPerPage}&filter=${this.filterType}`
                const res = await fetch(api, ApiServices.requestOptions("GET", "", token))
                const jsonObject = await res.json()

                if (res.status >= 400) {
                    this.progressShow = false
                    this.showSnackBar("שגיאה בקבלת נתונים", "error")
                } else if (res.status === 200) {
                    this.tickets = jsonObject.tickets
                    this.totalTickets = jsonObject.totalTickets
                }
            } catch (error) {
                this.showSnackBar("שגיאה בקבלת נתונים: " + error, "error")
            } finally {
                this.progressShow = false
            }
        },
        onSearchInput() {
            if (!this.search?.trim()) {
                this.debouncedSearch.cancel?.()
                this.getTicketsList()
                return
            }
            this.debouncedSearch()
        },
        async getSearchTickets() {
            if (!this.search) {
                this.getTicketsList()
                return
            }

            this.debouncedSearch.cancel?.();

            try {
                this.progressShow = true
                let token = localStorage.getItem("token")
                const JsonBody = JSON.stringify({ content: this.search.trim(), filter: this.filterType })
                let api = process.env.VUE_APP_BASE_URL + "/tickets/get_search_tickets"
                const res = await fetch(api, ApiServices.requestOptions("POST", JsonBody, token))
                const jsonObject = await res.json()

                if (res.status >= 400) {
                    this.showSnackBar("שגיאה בקבלת נתונים", "error")
                } else if (res.status === 200) {
                    this.tickets = jsonObject.tickets
                    this.totalTickets = jsonObject.totalTickets
                }
            } catch (error) {
                this.showSnackBar("שגיאה בחיפוש: " + error, "error")
            } finally {
                this.progressShow = false
            }
        },
        navigateToTicket(ticketId) {
            this.$router.push({ name: 'TicketDetails', params: { id: ticketId } })
        },
        calculatePosition(index) {
            return (this.currentPage - 1) * this.itemsPerPage + index + 1
        },
        isFileUrl(message) {
            if (!message) return false;
            // Check if message contains /uploads/ path or common file extensions
            const filePattern = /\/uploads\/|\.(?:jpg|jpeg|png|gif|pdf|doc|docx|txt|zip|rar|mp4|mp3|xlsx|xls|ppt|pptx)$/i;
            return filePattern.test(message);
        },
        // ── Presentation helpers (pure, no side effects) ──
        isUnread(ticket) {
            const last = ticket.responses[ticket.responses.length - 1];
            return last && last.read_at === null && last.responder_id.role !== this.userRole;
        },
        formatDate(d) {
            const date = new Date(d);
            const now = new Date();
            const diffMs = now - date;
            const diffMins = Math.floor(diffMs / 60000);
            const diffHours = Math.floor(diffMs / 3600000);
            const diffDays = Math.floor(diffMs / 86400000);
            if (diffMins < 1) return 'עכשיו';
            if (diffMins < 60) return `לפני ${diffMins} ד׳`;
            if (diffHours < 24) return `לפני ${diffHours} שע׳`;
            if (diffDays < 7) return `לפני ${diffDays} ימים`;
            return date.toLocaleDateString('he-IL');
        },
    },
    mounted() {
        this.getTicketsList()
        this.debouncedSearch = _.debounce(this.getSearchTickets, 900);

    },
}
</script>

<style scoped>
/* ─────────────────── TOKENS ─────────────────── */
.tk-page {
    --t-navy: #0d2c6d;
    --t-navy-soft: #142f78;
    --t-navy-deep: #081f54;
    --t-indigo: #3F51B5;
    --t-purple: #8066f0;
    --t-gold: #fdb721;
    --t-gold-deep: #d99b08;
    --t-gold-soft: rgba(253, 183, 33, 0.10);
    --t-green: #16804b;
    --t-green-soft: rgba(22, 128, 75, 0.10);
    --t-gray: #94a3b8;
    --t-gray-soft: rgba(148, 163, 184, 0.12);
    --t-bg: #f3f6f9;
    --t-surface: #ffffff;
    --t-border: #e3e8ef;
    --t-border-soft: #eef2f7;
    --t-text: #0f172a;
    --t-text-soft: #475569;
    --t-text-mute: #94a3b8;

    --t-shadow-sm: 0 1px 2px rgba(13, 44, 109, 0.04), 0 1px 3px rgba(13, 44, 109, 0.06);
    --t-shadow-md: 0 4px 14px rgba(13, 44, 109, 0.07), 0 2px 4px rgba(13, 44, 109, 0.04);
    --t-shadow-lg: 0 18px 40px -12px rgba(13, 44, 109, 0.18), 0 8px 16px -8px rgba(13, 44, 109, 0.10);
    --t-ease: cubic-bezier(0.22, 1, 0.36, 1);

    min-height: 100vh;
    padding-bottom: 60px;
    background: var(--t-bg);
    direction: rtl;
    font-family: 'Assistant', 'Heebo', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
}

/* ─────────────────── HEADER (gold flourish — Tickets theme) ─────────────────── */
.tk-header {
    background:
        radial-gradient(800px 200px at 90% -30%, rgba(253, 183, 33, 0.18), transparent 60%),
        linear-gradient(135deg, #0d2c6d 0%, #142f78 55%, #1a3a8a 100%);
    color: #fff;
    padding: 12px 0 14px;
    box-shadow: var(--t-shadow-md);
}

.tk-header__inner {
    max-width: 1300px;
    margin: 0 auto;
    padding: 0 28px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
}

.tk-header__title-wrap {
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;
}

.tk-title {
    margin: 0;
    font-size: 1.3rem;
    font-weight: 800;
    letter-spacing: -0.01em;
    line-height: 1.2;
}

.tk-total-pill {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 12px;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.10);
    border: 1px solid rgba(255, 255, 255, 0.20);
    color: #fff;
    font-size: 0.85rem;
    font-weight: 700;
    font-feature-settings: "tnum" 1;
}

.tk-total-pill ::v-deep .v-icon {
    color: rgba(255, 255, 255, 0.85) !important;
}

/* ─────────────────── MAIN ─────────────────── */
.tk-main {
    max-width: 1300px;
    margin: 22px auto 0;
    padding: 0 22px;
}

/* ─────────────────── TOOLBAR ─────────────────── */
.tk-toolbar {
    display: grid;
    grid-template-columns: minmax(260px, 1fr) auto auto;
    align-items: center;
    gap: 14px;
    margin-bottom: 18px;
}

.tk-search {
    position: relative;
    display: flex;
    align-items: center;
    height: 42px;
    padding: 0 12px;
    border-radius: 12px;
    background: var(--t-surface);
    border: 1px solid var(--t-border);
    transition: border-color 0.2s var(--t-ease), box-shadow 0.2s var(--t-ease);
}

.tk-search:focus-within {
    border-color: var(--t-navy);
    box-shadow: 0 0 0 4px rgba(13, 44, 109, 0.08);
}

.tk-search__icon {
    color: var(--t-text-mute) !important;
}

.tk-search__input {
    flex: 1;
    min-width: 0;
    padding: 0 9px;
    border: none;
    outline: none;
    background: transparent;
    color: var(--t-text);
    font-family: inherit;
    font-size: 0.91rem;
}

.tk-search__input::placeholder {
    color: var(--t-text-mute);
}

.tk-search__clear {
    width: 22px;
    height: 22px;
    display: grid;
    place-items: center;
    border: none;
    border-radius: 50%;
    background: var(--t-border-soft);
    color: var(--t-text-soft);
    cursor: pointer;
}

.tk-filter-chips {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
}

.tk-chip {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    padding: 7px 14px;
    border-radius: 999px;
    background: var(--t-surface);
    border: 1px solid var(--t-border);
    color: var(--t-text-soft);
    font-family: inherit;
    font-size: 0.85rem;
    font-weight: 600;
    cursor: pointer;
    white-space: nowrap;
    transition: all 0.2s var(--t-ease);
}

.tk-chip:hover {
    border-color: rgba(13, 44, 109, 0.25);
    color: var(--t-text);
}

.tk-chip__dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--t-border);
    transition: all 0.2s var(--t-ease);
}

.tk-chip__dot--all {
    background: var(--t-navy);
}

.tk-chip__dot--unread {
    background: var(--t-gold);
}

.tk-chip__dot--close {
    background: var(--t-gray);
}

.tk-chip--active {
    background: var(--t-navy);
    border-color: var(--t-navy);
    color: #fff;
    box-shadow: 0 6px 14px -4px rgba(13, 44, 109, 0.40);
}

.tk-chip--active .tk-chip__dot {
    background: var(--t-gold);
    box-shadow: 0 0 0 3px rgba(253, 183, 33, 0.18);
}

.tk-pagesize {
    display: flex;
    align-items: center;
    gap: 8px;
    color: var(--t-text-soft);
    font-size: 0.84rem;
    font-weight: 700;
}

.tk-pagesize__select {
    padding: 7px 12px;
    border: 1px solid var(--t-border);
    border-radius: 10px;
    background: var(--t-surface);
    color: var(--t-text);
    font-family: inherit;
    font-size: 0.87rem;
    font-weight: 700;
    cursor: pointer;
    outline: none;
}

.tk-pagesize__select:focus {
    border-color: var(--t-navy);
    box-shadow: 0 0 0 4px rgba(13, 44, 109, 0.08);
}

/* ─────────────────── TICKET LIST ─────────────────── */
.tk-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.tk-ticket {
    position: relative;
    display: block;
    background: var(--t-surface);
    border: 1px solid var(--t-border-soft);
    border-radius: 14px;
    cursor: pointer;
    overflow: hidden;
    transition: transform 0.25s var(--t-ease), box-shadow 0.25s var(--t-ease), border-color 0.25s var(--t-ease);
    animation: tk-slideIn 0.4s var(--t-ease) backwards;
    animation-delay: calc(var(--i, 0) * 0.04s);
    box-shadow: var(--t-shadow-sm);
}

@keyframes tk-slideIn {
    from {
        opacity: 0;
        transform: translateY(8px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.tk-ticket:hover {
    transform: translateY(-2px);
    box-shadow: var(--t-shadow-lg);
    border-color: rgba(13, 44, 109, 0.14);
}

/* RTL leading-edge strip — color encodes status */
.tk-ticket__strip {
    position: absolute;
    top: 0;
    bottom: 0;
    right: 0;
    width: 4px;
    background: var(--t-border);
    transition: background 0.3s var(--t-ease), width 0.3s var(--t-ease);
}

.tk-ticket--unread .tk-ticket__strip {
    width: 5px;
    background: linear-gradient(180deg, var(--t-gold) 0%, var(--t-gold-deep) 100%);
    box-shadow: -1px 0 12px rgba(253, 183, 33, 0.32);
}

.tk-ticket--closed .tk-ticket__strip {
    background: linear-gradient(180deg, var(--t-gray) 0%, #6b7280 100%);
}

/* Subtle bg tint when unread */
.tk-ticket--unread {
    background: linear-gradient(270deg, rgba(253, 183, 33, 0.04) 0%, var(--t-surface) 60%);
    border-color: rgba(253, 183, 33, 0.28);
}

.tk-ticket--closed {
    opacity: 0.84;
}

.tk-ticket--closed:hover {
    opacity: 1;
}

.tk-ticket__body {
    display: grid;
    grid-template-columns: auto 1fr auto auto;
    align-items: center;
    gap: 16px;
    padding: 16px 22px 16px 18px;
}

/* Number badge */
.tk-ticket__num {
    flex-shrink: 0;
    width: 44px;
    height: 44px;
    border-radius: 12px;
    display: grid;
    place-items: center;
    background: linear-gradient(135deg, var(--t-navy) 0%, var(--t-navy-soft) 100%);
    color: #fff;
    font-weight: 800;
    font-size: 0.95rem;
    box-shadow: 0 4px 10px -3px rgba(13, 44, 109, 0.35);
    font-feature-settings: "tnum" 1;
}

.tk-ticket--unread .tk-ticket__num {
    background: linear-gradient(135deg, var(--t-gold-deep) 0%, var(--t-gold) 100%);
    color: #2d1f00;
    box-shadow: 0 4px 10px -3px rgba(253, 183, 33, 0.45);
}

.tk-ticket__num--closed,
.tk-ticket--closed .tk-ticket__num {
    background: linear-gradient(135deg, #9ca3af 0%, #c1c7d0 100%) !important;
    color: #fff !important;
    box-shadow: 0 4px 10px -3px rgba(148, 163, 184, 0.35) !important;
}

/* Content */
.tk-ticket__content {
    min-width: 0;
    flex: 1;
}

.tk-ticket__header-row {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 4px;
}

.tk-ticket__id {
    display: inline-block;
    padding: 2px 10px;
    border-radius: 6px;
    background: rgba(13, 44, 109, 0.08);
    color: var(--t-navy);
    font-weight: 800;
    font-size: 0.78rem;
    letter-spacing: 0.02em;
    direction: ltr;
}

.tk-ticket__id--closed,
.tk-ticket--closed .tk-ticket__id {
    background: var(--t-gray-soft);
    color: var(--t-text-mute);
}

.tk-ticket__subject {
    margin: 0;
    font-size: 1rem;
    font-weight: 700;
    color: var(--t-text);
    letter-spacing: -0.005em;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    display: flex;
    align-items: center;
    gap: 7px;
    min-width: 0;
}

.tk-ticket--closed .tk-ticket__subject {
    color: var(--t-text-mute);
}

.tk-ticket__unread-dot {
    flex-shrink: 0;
    display: inline-block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--t-gold);
    box-shadow: 0 0 0 4px rgba(253, 183, 33, 0.20);
    animation: tk-pulse 2s ease-in-out infinite;
}

@keyframes tk-pulse {

    0%,
    100% {
        box-shadow: 0 0 0 4px rgba(253, 183, 33, 0.20);
    }

    50% {
        box-shadow: 0 0 0 7px rgba(253, 183, 33, 0.04);
    }
}

.tk-ticket__preview {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 0.86rem;
    line-height: 1.5;
    color: var(--t-text-soft);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.tk-ticket--closed .tk-ticket__preview {
    color: var(--t-text-mute);
}

.tk-ticket__preview-icon {
    color: var(--t-navy) !important;
    flex-shrink: 0;
}

.tk-ticket__file-indicator {
    color: var(--t-navy);
    font-weight: 700;
}

/* Side meta */
.tk-ticket__side {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 8px;
    flex-shrink: 0;
}

.tk-ticket__date {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 0.78rem;
    color: var(--t-text-mute);
    font-weight: 600;
}

.tk-ticket__date ::v-deep .v-icon {
    color: var(--t-text-mute) !important;
}

.tk-ticket__pills {
    display: flex;
    gap: 6px;
}

.tk-pill {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 3px 9px;
    border-radius: 999px;
    font-size: 0.72rem;
    font-weight: 800;
    letter-spacing: 0.02em;
    white-space: nowrap;
}

.tk-pill ::v-deep .v-icon {
    color: inherit !important;
}

.tk-pill--open {
    color: var(--t-green);
    background: var(--t-green-soft);
}

.tk-pill--closed {
    color: var(--t-text-mute);
    background: var(--t-gray-soft);
}

.tk-pill--unread {
    color: var(--t-gold-deep);
    background: var(--t-gold-soft);
}

.tk-pill--read {
    color: var(--t-text-mute);
    background: var(--t-gray-soft);
}

/* Chevron indicator (RTL: points left = forward) */
.tk-ticket__chevron {
    color: var(--t-text-mute);
    display: grid;
    place-items: center;
    transition: transform 0.25s var(--t-ease), color 0.25s var(--t-ease);
}

.tk-ticket:hover .tk-ticket__chevron {
    color: var(--t-navy);
    transform: translateX(-4px);
}

.tk-ticket__chevron ::v-deep .v-icon {
    color: inherit !important;
}

/* ─────────────────── EMPTY ─────────────────── */
.tk-empty {
    padding: 70px 20px;
    text-align: center;
    background: var(--t-surface);
    border: 1px dashed var(--t-border);
    border-radius: 14px;
}

.tk-empty__icon {
    width: 76px;
    height: 76px;
    margin: 0 auto 14px;
    display: grid;
    place-items: center;
    border-radius: 50%;
    color: var(--t-navy);
    background: rgba(13, 44, 109, 0.08);
}

.tk-empty__title {
    margin: 0 0 4px;
    color: var(--t-text);
    font-size: 1.05rem;
    font-weight: 700;
}

.tk-empty__hint {
    margin: 0;
    color: var(--t-text-mute);
    font-size: 0.88rem;
}

/* ─────────────────── PAGINATION ─────────────────── */
.tk-pagination-wrap {
    display: flex;
    justify-content: center;
    margin-top: 20px;
}

::v-deep .tk-pagination-wrap .v-pagination__item,
::v-deep .tk-pagination-wrap .v-pagination__navigation {
    border: 1px solid var(--t-border) !important;
    box-shadow: none !important;
    color: var(--t-text-soft) !important;
    font-family: inherit !important;
    font-weight: 700 !important;
}

::v-deep .tk-pagination-wrap .v-pagination__item--active {
    background: var(--t-navy) !important;
    color: #fff !important;
    border-color: var(--t-navy) !important;
    box-shadow: 0 4px 10px -2px rgba(13, 44, 109, 0.40) !important;
}

/* ─────────────────── RESPONSIVE ─────────────────── */
@media (max-width: 900px) {
    .tk-header__inner {
        padding: 0 18px;
    }

    .tk-title {
        font-size: 1.1rem;
    }

    .tk-main {
        padding: 0 12px;
        margin-top: 16px;
    }

    .tk-toolbar {
        grid-template-columns: 1fr;
    }

    .tk-ticket__body {
        grid-template-columns: auto 1fr;
        grid-template-rows: auto auto;
        gap: 12px;
        padding: 14px 18px 14px 14px;
    }

    .tk-ticket__side {
        grid-column: 1 / -1;
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
    }

    .tk-ticket__chevron {
        display: none;
    }
}

@media (max-width: 480px) {
    .tk-ticket__num {
        width: 38px;
        height: 38px;
        font-size: 0.85rem;
    }

    .tk-ticket__subject {
        font-size: 0.92rem;
    }

    .tk-ticket__preview {
        font-size: 0.8rem;
    }

    .tk-pill {
        padding: 2px 7px;
        font-size: 0.68rem;
    }
}

@media (prefers-reduced-motion: reduce) {

    .tk-ticket,
    .tk-ticket__unread-dot {
        animation: none;
    }
}
</style>